import { apiClient } from './client';
import type { ApiSuccess, PaginationMeta } from '../types';

export interface CmsCharger {
  id: number; chargerIndex: number | null; chargerType: string | null; capacityKw: number; serialNumber: string | null;
  cmsChargerId: string | null; status: string; liveStatus: 'online' | 'offline' | 'faulty' | 'unknown';
  lastHeartbeatAt: string | null; lastSyncedAt: string | null;
}
export interface CmsStatusResult { configured: boolean; provider: string; connectionId: number | null; chargers: CmsCharger[] }
export interface CmsConsumption { configured: boolean; provider: string; chargerCount: number; daily: number; monthly: number; ytd: number }
export interface AmReconciliation {
  id: number; electricityBillId: number; stationId: number; stationName: string | null; monthYear: string;
  billUnits: number; cmsUnits: number; variancePercent: number; thresholdPercent: number;
  status: 'good_to_go' | 'flagged' | 'verified'; remarks: string | null; verifiedBy: number | null; verifiedAt: string | null; createdAt: string;
}

export interface CmsConnection {
  id: number; name: string; provider: string; baseUrl: string | null; authType: 'bearer' | 'api_key' | 'basic';
  authHeader: string | null; apiKeyMasked: string | null; hasApiKey: boolean; clientId: number | null; status: 'active' | 'inactive'; createdAt: string;
}
export interface CmsConnectionInput {
  name: string; provider: string; baseUrl?: string; authType?: 'bearer' | 'api_key' | 'basic'; authHeader?: string; apiKey?: string; clientId?: number | null; status?: 'active' | 'inactive';
}

export const amCmsConnectionsApi = {
  async list(params: Record<string, unknown> = {}): Promise<{ data: CmsConnection[]; meta: PaginationMeta }> {
    const { data } = await apiClient.get<ApiSuccess<CmsConnection[]>>('/am-cms-connections', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  create: async (body: CmsConnectionInput) => (await apiClient.post<ApiSuccess<CmsConnection>>('/am-cms-connections', body)).data.data,
  update: async (id: number, body: Partial<CmsConnectionInput>) => (await apiClient.put<ApiSuccess<CmsConnection>>(`/am-cms-connections/${id}`, body)).data.data,
  remove: async (id: number) => { await apiClient.delete(`/am-cms-connections/${id}`); },
  map: async (stationId: number, connectionId: number | null) => (await apiClient.post<ApiSuccess<{ stationId: number; connectionId: number | null }>>('/am-cms-connections/map', { stationId, connectionId })).data.data,
};

export const amCmsApi = {
  status: async (stationId: number) => (await apiClient.get<ApiSuccess<CmsStatusResult>>('/am-cms/status', { params: { stationId } })).data.data,
  consumption: async (stationId: number) => (await apiClient.get<ApiSuccess<CmsConsumption>>('/am-cms/consumption', { params: { stationId } })).data.data,
  sync: async (stationId?: number) => (await apiClient.post<ApiSuccess<{ configured: boolean; provider: string; synced: number }>>('/am-cms/sync', { stationId })).data.data,
  async reconciliations(params: Record<string, unknown> = {}): Promise<{ data: AmReconciliation[]; meta: PaginationMeta }> {
    const { data } = await apiClient.get<ApiSuccess<AmReconciliation[]>>('/am-cms/reconciliations', { params });
    return { data: data.data, meta: data.meta as PaginationMeta };
  },
  reconcile: async (billId: number, thresholdPercent?: number) => (await apiClient.post<ApiSuccess<AmReconciliation>>(`/am-cms/reconcile/${billId}`, { thresholdPercent })).data.data,
  verify: async (id: number, remarks?: string) => (await apiClient.post<ApiSuccess<AmReconciliation>>(`/am-cms/reconciliations/${id}/verify`, { remarks })).data.data,
};
